import { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Spacer,
  Stack,
  Text,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import { AudioDisplay } from "./utils/audio-display";
import TranscriptNotesCard from "./display-notes";
import PrimaryButton from "../../../primary-button";
import { COLORS } from "../../../styles/colors";
import DynamicModalForTwo from "../dynamic-modal-for-two";
import { useLayoutEffect } from "react"; // Import useLayoutEffect
import DotElasticAnimation from "../../../dot-listening";
import {
  GetDoctorNotes,
  updateTranscriptNotes,
} from "../../../../middleware/services/visits/patient-overview/updatedoctornotes";
import Loader from "../../../loader/loader";
import useLoader from "../../../loader/loaderstates";
import DisplayNotesModal from "./display-notes-modal";
import LangDropdownWithID from "./lang-selector";

function MobileTranscribeNotes({
  patientId,
  doctorId,
  appointmentId,
  showSuccessToast,
  showErrorToast,
  closeNotesModal,
  fetchPatientHealthData,
}) {
  //------------------------Language Selector for multilingual


  // const languageOptions = [
  //   "English",
  //   "Hindi",
  //   "Tamil",
  //   "Telugu",
  //   "Kannada",
  //   "Malayalam",
  // ]; // have all keys dynamically from languageCodeMap

  const languageCodeMap = {
    "Hindi": "hi-IN",
    "Bengali": "bn-IN",
    "Tamil": "ta-IN",
    "Telugu": "te-IN",
    "Kannada": "kn-IN",
    "Malayalam": "ml-IN",
    "Gujarati": "gu-IN",
    "Marathi": "mr-IN",
    "Punjabi": "pa-IN",
    "English (US)": "en-US",
    "English (UK)": "en-GB",
    "Spanish (Spain)": "es-ES",
    "French (France)": "fr-FR",
    "German (Germany)": "de-DE",
    "Italian (Italy)": "it-IT",
    "Chinese (Simplified)": "zh-CN",
    "Arabic (Standard)": "ar-SA",
  };

  function setLanguageOptions() {
    // Map over the keys of languageCodeMap to get an array of language labels
    const languageOptions = Object.keys(languageCodeMap);
    return languageOptions;
  }
  
  const languageOptions = setLanguageOptions();

  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("lastSavedPreference") ? localStorage.getItem("lastSavedPreference") :"English");
  const [languageCode, setLanguageCode] = useState(languageCodeMap[localStorage.getItem("lastSavedPreference")] ? languageCodeMap[localStorage.getItem("lastSavedPreference")] : "en-US");
  const [preferences, setPreferences] = useState(() => {
    const savedPrefs = localStorage.getItem("preferences");
    return savedPrefs ? JSON.parse(savedPrefs) : [];
  });

  const handleChangeLanguage = (selectedOption) => {
    const selectedLanguage = selectedOption.value;
    setSelectedLanguage(selectedLanguage);
    const languageCode = languageCodeMap[selectedLanguage] || "en-US";
    setLanguageCode(languageCode);
    // console.log(selectedLanguage)
    // localStorage.setItem("lastSavedPreference", JSON.stringify(selectedLanguage));
    localStorage.setItem("lastSavedPreference", selectedLanguage);
    showSuccessToast("Language Changed, Please click mic to start recording again.")
  };

  const handleStarToggle = (language) => {
    let updatedPreferences = [...preferences];
    if (updatedPreferences.includes(language)) {
      updatedPreferences = updatedPreferences.filter((pref) => pref !== language);
    } else {
      updatedPreferences.push(language);
    }
    setPreferences(updatedPreferences);
    localStorage.setItem("preferences", JSON.stringify(updatedPreferences));
  };

  const sortedOptions = [
    ...preferences.map((pref) => ({ value: pref, starred: true })),
    ...languageOptions
      .filter((lang) => !preferences.includes(lang))
      .map((lang) => ({ value: lang, starred: false })),
  ];

  //-----------------------Testing data
  const demoText2 =
    "What brings you in today? For the past few days, I've been feeling really tired and short of breath, especially when I walk or do anything physical. I also have this persistent cough that just won't go away. Do you have any chest pain or fever along with these symptoms? I haven't had any chest pain, but I've noticed I sometimes feel a bit feverish, though I haven't checked my temperature. Have you ever experienced anything like this before? Not really, but I do get seasonal allergies sometimes. Do you have any other medical conditions, like asthma or high blood pressure? I was diagnosed with high blood pressure about two years ago, but I have been taking my Telma H medication irregularly. Any recent changes in your weight, appetite, or sleep patterns? I've lost a little weight recently, but I haven't been eating much because I've been feeling so tired. Let's talk about your vitals; when was the last time you had them checked? It's been a while—I think the last time was at my annual checkup last year. Have you had all your vaccinations, like the flu shot or the COVID-19 vaccine? Yes, I've had both of those, and my last flu shot was just a few months ago. Do you have any known allergies, either to medications or anything environmental? I'm allergic to penicillin, but nothing else that I know of. Have you had any surgeries in the past? I had my appendix removed when I was in college around 3 years ago, but no surgeries since then. Are you currently taking any medications other than the one for high blood pressure? No, just the one for blood pressure, but like I said, I haven't been consistent with it. Can you tell me a little about your lifestyle—do you smoke, drink, or have any other habits I should know about? I used to smoke, but I quit five years ago. I don't drink often, maybe a glass of wine here and there. I'm not very active lately because I've been so tired, but I used to walk every evening. Have you been exposed to anyone who's been sick recently? Not that I know of, but a lot of people at work have been coughing lately. Based on what you've told me, it sounds like you might have a respiratory infection, possibly bronchitis, and the shortness of breath could be related to your high blood pressure as well. I'd like to order some blood work and a chest X-ray to confirm, but in the meantime, I'll prescribe you Levocetirizine, an inhaler to help with the breathing and a cough suppressant. Make sure to drink plenty of fluids and rest as much as you can. I also want you to restart your blood pressure medication and take it consistently. If your symptoms worsen or you develop a fever, let me know immediately. Thank you, Doctor, I'll do that. I appreciate your help.";

    const demoText = "डॉक्टर जी, पिछले कुछ दिनों से मुझे बहुत थकान हो रही है और सांस लेने में तकलीफ हो रही है, खासकर जब मैं चलता हूं या कोई शारीरिक काम करता हूं। साथ ही, मुझे लगातार खांसी भी हो रही है जो ठीक नहीं हो रही है। क्या आपको सीने में दर्द या बुखार भी हो रहा है? नहीं डॉक्टर साहब, सीने में दर्द नहीं है लेकिन कभी-कभी मुझे थोड़ा बुखार सा लग रहा है, हालांकि मैंने तापमान नहीं लिया है। क्या आपको पहले कभी ऐसा अनुभव हुआ है? नहीं डॉक्टर साहब, लेकिन मुझे कभी-कभी मौसमी एलर्जी होती है। क्या आपको कोई अन्य स्वास्थ्य समस्या है, जैसे अस्थमा या उच्च रक्तचाप? मुझे लगभग दो साल पहले उच्च रक्तचाप हुआ था, लेकिन मैं अपनी दवा तेलमा एच अनियमित रूप से ले रहा हूं। क्या हाल ही में आपके वजन, भूख या नींद के पैटर्न में कोई बदलाव आया है? मैंने हाल ही में थोड़ा वजन कम किया है, लेकिन मैं बहुत कम खा रहा हूं क्योंकि मैं बहुत थका हुआ महसूस कर रहा हूं। आइए आपके स्वास्थ्य संबंधी मापदंडों के बारे में बात करते हैं; आपने आखिरी बार कब जांच करवाई थी? बहुत समय हो गया है - मुझे लगता है कि आखिरी बार पिछले साल मेरी वार्षिक जांच के दौरान हुआ था। क्या आपको सभी टीके लग गए हैं, जैसे फ्लू का टीका या कोविड-19 का टीका? जी हां, मुझे दोनों टीके लग गए हैं, और मेरा आखिरी फ्लू का टीका कुछ महीने पहले ही लगा था। क्या आपको कोई ज्ञात एलर्जी है, चाहे वह दवाओं से हो या किसी पर्यावरणीय चीज़ से? मुझे पेनिसिलिन से एलर्जी है, लेकिन मुझे नहीं पता कि और क्या है। क्या आपको अतीत में कोई सर्जरी हुई है? कॉलेज में लगभग 3 साल पहले मेरा अपेंडिक्स निकाला गया था, लेकिन तब से कोई सर्जरी नहीं हुई है। क्या आप वर्तमान में उच्च रक्तचाप की दवा के अलावा कोई अन्य दवा ले रहे हैं? नहीं, सिर्फ रक्तचाप की दवा, लेकिन जैसा कि मैंने कहा, मैं इसका सेवन लगातार नहीं कर रहा हूं। क्या आप मुझे अपनी जीवनशैली के बारे में थोड़ा बता सकते हैं - क्या आप धूम्रपान करते हैं, शराब पीते हैं, या कोई अन्य आदत है जिसके बारे में मुझे पता होना चाहिए? मैं पहले धूम्रपान करता था, लेकिन मैंने पांच साल पहले छोड़ दिया था। मैं अक्सर शराब नहीं पीता, शायद कभी-कभी एक गिलास वाइन। मैं हाल ही में बहुत सक्रिय नहीं रहा क्योंकि मैं बहुत थका हुआ हूं, लेकिन मैं हर शाम टहलने जाता था। क्या आप हाल ही में किसी ऐसे व्यक्ति के संपर्क में आए हैं जो बीमार रहा हो? मुझे नहीं पता, लेकिन हाल ही में काम पर बहुत से लोग खांसी कर रहे हैं। आपके द्वारा बताई गई बातों के आधार पर, ऐसा लगता है कि आपको श्वसन संक्रमण हो सकता है, संभवतः ब्रोंकाइटिस, और सांस लेने में तकलीफ आपके उच्च रक्तचाप से भी संबंधित हो सकती है। मैं कुछ रक्त परीक्षण और सीने का एक्स-रे करवाना चाहूंगा, लेकिन इस बीच, मैं आपको लेवोसेटिरिज़िन, सांस लेने में मदद करने के लिए एक इनहेलर और खांसी की दवाई लिखूंगा। भरपूर मात्रा में पानी पिएं और जितना हो सके आराम करें। मैं चाहता हूं कि आप अपनी रक्तचाप की दवा फिर से शुरू करें और इसे लगातार लें। यदि आपके लक्षण बिगड़ते हैं या आपको बुखार आता है, तो मुझे तुरंत बताएं। धन्यवाद, डॉक्टर, मैं ऐसा करूंगा। आपका बहुत धन्यवाद।"

  const germanText =
    "Was führt Sie heute zu mir? Seit ein paar Wochen habe ich immer wieder Magenschmerzen, vor allem nach dem Essen. Außerdem habe ich häufig Sodbrennen und ein Völlegefühl, selbst wenn ich nur kleine Portionen esse. Haben Sie auch Übelkeit oder Erbrechen bemerkt? Ja, gelegentlich ist mir übel, aber ich habe mich nicht übergeben. Haben Sie Veränderungen beim Stuhlgang bemerkt, wie Verstopfung oder Durchfall? Eigentlich nicht, aber ich habe bemerkt, dass ich manchmal aufgebläht bin. Haben Sie irgendwelche bekannten medizinischen Bedingungen wie Gastritis oder Diabetes? Vor etwa einem Jahr wurde bei mir eine Gastritis diagnostiziert, aber ich habe keine regelmäßige Behandlung durchgeführt. Haben Sie irgendwelche Medikamente eingenommen? Ich habe Pantoprazol für einige Monate genommen, aber ich habe es vor ein paar Monaten abgesetzt, weil es mir besser ging. Haben Sie in letzter Zeit Gewicht verloren oder eine Veränderung des Appetits bemerkt? Ich habe etwas Gewicht verloren, vielleicht zwei oder drei Kilo, weil ich weniger Appetit habe. Haben Sie irgendwelche Operationen in der Vergangenheit gehabt? Ja, ich hatte vor fünf Jahren eine Gallenblasenentfernung. Gab es irgendwelche Komplikationen danach? Nein, alles verlief ohne Probleme. Können Sie mir sagen, wann Sie das letzte Mal Ihre Vitalwerte überprüfen ließen? Vor etwa drei Monaten, bei einer Routineuntersuchung. Wissen Sie, wie Ihre Werte damals waren? Mein Blutdruck war etwas erhöht, etwa 140/90, und mein Puls war normal, um die 75 Schläge pro Minute. Meine Körpertemperatur war normal, und es gab keine Auffälligkeiten im Blutbild. Rauchen oder trinken Sie? Ich rauche nicht, aber ich trinke gelegentlich ein Glas Bier oder Wein. Haben Sie in letzter Zeit etwas Stress oder Veränderungen in Ihrem Lebensstil erlebt? Ja, ich hatte beruflich viel Stress in den letzten Monaten, und ich denke, das hat meine Essgewohnheiten beeinträchtigt. Wurden Sie kürzlich gegen Krankheiten wie Grippe oder COVID-19 geimpft? Ja, ich habe beide Impfungen erhalten, und meine letzte Grippeimpfung war vor vier Monaten. Sind Sie auf irgendwelche Medikamente oder Lebensmittel allergisch? Nein, keine Allergien, soweit ich weiß. Haben Sie jemals ähnliche Symptome in der Vergangenheit gehabt? Vor ein paar Jahren hatte ich gelegentlich ähnliche Beschwerden, aber sie waren nicht so stark. Basierend auf Ihren Angaben vermute ich, dass Sie möglicherweise an einer Refluxkrankheit oder einer Verschlechterung Ihrer Gastritis leiden. Ich möchte eine Magenspiegelung vorschlagen, um die Ursache abzuklären. Bis dahin werde ich Ihnen Pantoprazol verschreiben und Ihnen raten, säurehaltige und fettige Lebensmittel zu vermeiden. Trinken Sie viel Wasser, und versuchen Sie, kleinere Mahlzeiten über den Tag verteilt zu essen. Wenn sich Ihre Symptome verschlimmern, insbesondere wenn Sie Blut im Stuhl bemerken oder starke Schmerzen haben, melden Sie sich bitte sofort. Danke, Doktor, ich werde das tun. Vielen Dank für Ihre Hilfe.";

  //-----------------------New Audio
  const [recordingIndex, setRecordingIndex] = useState(null); // Track which input field is recording
  const [isRecording, setIsRecording] = useState(false);
  const [voice, setVoice] = useState("");
  const [initialVoice, setInitialVoice] = useState("");

  const [recognition, setRecognition] = useState(null);

  const startRecording = (index) => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert("Your browser does not support voice recognition.");
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const newRecognition = new SpeechRecognition();
    // console.log("setting the language", languageCode)
    // console.log("fetching the language code", languageCodeMap[localStorage.getItem("lastSavedPreference")])
    // console.log("fetching the language code from local storage", localStorage.getItem("lastSavedPreference"))
    newRecognition.lang = languageCode ? languageCode :  "en-US";
    newRecognition.interimResults = true;
    newRecognition.continuous = true;

    newRecognition.onstart = () => {
      setRecordingIndex(index); // Indicate recording started for specific input
      setIsRecording(true); // Show recording status
    };

    newRecognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(" ");
      // handleAnswerChangethirdStep(index, secondChats[index].answer + " " + transcript); // Append transcript for step 3
      // setVoice(voice + " " + transcript)
      // setVoice((prev) => prev + " " + transcript);
      // setVoice(initialVoice+ " " +transcript);
      setVoice(transcript);
    };

    newRecognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      stopRecording(); // Stop recording on error
    };

    newRecognition.onend = () => {
      // console.log("called from onend")
      stopRecording(); // Stop recording when it ends
    };

    newRecognition.start();
    setRecognition(newRecognition); // Save recognition instance for stopping later
  };
  const stopRecording = (update = false) => {
    console.log("recording stopped");
    if (recognition) {
      recognition.stop();
    }
    setRecordingIndex(null); // Reset recording index
    setIsRecording(false); // Hide recording status
    setRecognition(null); // Clear recognition instance
    if (update) {
      if (initialVoice) {
        console.log("saving with preset", initialVoice + voice);
        UpdateTranscript(initialVoice + " " + voice);
      } else {
        console.log("saving without preset", voice);
        UpdateTranscript(voice);
      }
      // UpdateTranscript(voice)
    }
  };

  const stopRecordingAndReset = () => {
    console.log("recording reset");
    if (recognition) {
      recognition.stop();
    }
    setRecordingIndex(null); // Reset recording index
    setIsRecording(false); // Hide recording status
    setRecognition(null); // Clear recognition instance
  };

  // useEffect(() => {
  //   startRecording(1);
  // }, []);

  useEffect(() => {
    fetchDoctorNotes()
      .then(() => {
        startRecording(1); // Start recording after successful fetch
      })
      .catch((error) => {
        console.error("Error fetching doctor notes:", error);
      });
  }, []);

  useEffect(() => {
    // ResetTranscriptionNotesModal().then(() => {
    //   closeNotesModal();
    // });
    ResetTranscriptionNotesModal()
  }, [languageCode]);
  

  //------------------------Store and fetch the transcription

  const { isLoading, showLoader, dismissLoader } = useLoader();
  const fetchDoctorNotes = async () => {
    const inputToFetch = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
      },
    };

    showLoader();
    try {
      const data = await GetDoctorNotes(inputToFetch.input);
      const saved_transcript = data?.output?.data?.transcription || "";
      console.log(saved_transcript);
      if (saved_transcript) {
        setInitialVoice(saved_transcript);
      }
    } catch (error) {
      console.error("Error fetching Doctor Notes data:", error);
    } finally {
      dismissLoader();
    }
  };

  const UpdateTranscript = async (data) => {
    const inputToSave = {
      input: {
        appointmentid: appointmentId,
        type: "transcription",
        transcription: data,
      },
    };
    console.log(inputToSave);

    showLoader();
    try {
      const result = await updateTranscriptNotes(inputToSave.input);
      if (result) {
        showSuccessToast("Transcription Saved successfully");
      }
    } catch (error) {
      console.error("Error in Update Transcription Notes:", error);
      showErrorToast("Error Happened While Saving.");
    } finally {
      dismissLoader();
    }
  };

  const ResetTranscript = async (data) => {
    const inputToSave = {
      input: {
        appointmentid: appointmentId,
        type: "transcription",
        transcription: "",
      },
    };
    console.log(inputToSave);

    showLoader();
    try {
      const result = await updateTranscriptNotes(inputToSave.input);
      if (result) {
        showSuccessToast("Transcription reset successfully");
      }
    } catch (error) {
      console.error("Error in Update Transcription Notes:", error);
      showErrorToast("Error Happened While Saving.");
    } finally {
      dismissLoader();
    }
  };

  const containerRef = useRef(null); // Ref for the container to scroll

  const [recording, setRecording] = useState(false);

  // Auto-scrolling logic
  useLayoutEffect(() => {
    if (containerRef.current) {
      // Ensure it scrolls after DOM update
      setTimeout(() => {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }, 0); // 0 delay to execute after the DOM update
    }
  }, [voice]); // Trigger auto-scrolling when voiceInput updates

  // Helper function to format time
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60); // Remove decimals
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const [combinedInput, setCombinedInput] = useState([]);

  const openNotesModal = () => {
    airaTranscribeGenerateNotes.current.openModal();
    if (isRecording) {
      stopRecording();
    }
    // setCombinedInput(concatenateChunks(voiceInput));
  };

  const saveAndCloseNotesModal = () => {
    if (isRecording) {
      console.log("recording was on, closing");
      stopRecording(true);
    }
    closeNotesModal();
  };

  const ResetTranscriptionNotesModal = () => {
    setVoice("");
    setInitialVoice("");
    ResetTranscript("");
    if (isRecording) {
      console.log("recording was on, closing");
      stopRecordingAndReset();
    }
  };

  //-----------------------GPU Inference
  const recorderRef = useRef(null);

  const [chunks, setChunks] = useState([]);

  const [stream, setStream] = useState(null);
  // const audioContextRef = useRef(null);

//   useEffect(() => {
//     if (recorderRef.current) return;

//     if (navigator.mediaDevices.getUserMedia) {
//       navigator.mediaDevices
//         .getUserMedia({ audio: true })
//         .then((stream) => {
//           setStream(stream);

//           recorderRef.current = new MediaRecorder(stream);
//           // audioContextRef.current = new AudioContext({
//           //   sampleRate: WHISPER_SAMPLING_RATE,
//           // });

//           recorderRef.current.onstart = () => {
//             setRecording(true);
//             setChunks([]);
//           };
//           recorderRef.current.ondataavailable = (e) => {
//             if (e.data.size > 0) {
//               setChunks((prev) => [...prev, e.data]);
//             } else {
//               setTimeout(() => {
//                 recorderRef.current.requestData();
//               }, 25);
//             }
//           };

//           recorderRef.current.onstop = () => {
//             setRecording(false);
//           };
//         })
//         .catch((err) => console.error("The following error occurred: ", err));
//     } else {
//       console.error("getUserMedia not supported on your browser!");
//     }

//     return () => {
//       recorderRef.current?.stop();
//       recorderRef.current = null;
//     };
//   }, []);

  const { colorMode } = useColorMode();
  const textColor = colorMode === "light" ? "gray.800" : "gray.200";
  const bgColor = colorMode === "light" ? "white" : "gray.900";

  //----------------------Create notes

  //-------------------------AIRA Notes Transcription
  const airaTranscribeGenerateNotes = useRef(null);

  return (
    <Flex
      w="full"
      h="full"
      justify="center"
      align="center"
      textAlign="center"
      zIndex={9999}
    >
      <Stack spacing={4}>
        <Heading as="h1" fontWeight="bold" fontSize="2rem" mb={1}>
          AIRA - Notes
        </Heading>
        <Text>
          Hello Doctor, AIRA is always ready to assist. Just hit the start
          button, and the assistant will start listening to your conversation.
        </Text>
        <Flex justify="center" align="center">
          <Box w="45%">
            <LangDropdownWithID
              menuList={{ pos: 'relative', right: '75px' }}
              placeholder={selectedLanguage ? selectedLanguage : "Choose language"}
              options={sortedOptions}
              onClick={handleChangeLanguage}
              onStarToggle={handleStarToggle}
            />
          </Box>
        </Flex>
        <Flex justify="center" align="center">
          {isRecording ? (
            <Box
              onClick={isRecording ? stopRecording : () => startRecording(1)}
              cursor="pointer"
            >
              <DotElasticAnimation />
            </Box>
          ) : (
            <Image
              // pos="absolute"
              w="16px"
              // top="45px"
              // right="16px"
              opacity="0.5rem"
              cursor="pointer"
              src={
                isRecording
                  ? "/assets/svgs/mute.svg" // Stop icon
                  : "/assets/svgs/un-mute.svg" // Start icon
              }
              alt={isRecording ? "Stop Recording" : "Start Recording"}
              onClick={isRecording ? stopRecording : () => startRecording(1)}
              // zIndex="99"
              transition="all 0.2s ease-in-out" // Add a smooth transition
            />
          )}
        </Flex>
        <Flex justify="center" align="center">
          {/* <AudioDisplay
            // maxW="600px"
            // w="full"
            mx="auto"
            borderRadius="lg"
            stream={stream}
          /> */}
        </Flex>

        <Box
          ref={containerRef}
          position="relative"
          alignSelf={"center"}
          w="640px"
          h="200px"
          overflowY="auto"
          border="1px"
          borderRadius="lg"
          p={2}
          bg="gray.50"
          boxShadow="md"
          borderColor="gray.300"
          fontFamily="Arial, sans-serif"
        >
          <Text
            textAlign="left"
            fontSize="16px"
            whiteSpace="normal"
            wordBreak="break-word"
            color="black"
            fontWeight="normal"
            lineHeight="1.5"
            opacity={0.9}
            animation="typing 3s steps(50) infinite, fadeIn 1.5s ease-in-out;"
          >
            {initialVoice ? initialVoice + " " + voice : voice}
          </Text>
        </Box>
        <Flex w="100%" justifyContent="space-between">
          <PrimaryButton
            variant="deleteBtn"
            buttonText="Save & Close"
            w="200px"
            // mx="auto"
            onClick={saveAndCloseNotesModal}
          />

          <PrimaryButton
            // variant="grayBtn"
            buttonText="Reset Transcription"
            w="200px"
            ml="12px"
            // mx="auto"
            onClick={ResetTranscriptionNotesModal}
          />

          <Spacer />

          <Box
            as="button"
            borderRadius="md"
            // onClick={() => airaTranscribeGenerateNotes.current.openModal()}
            onClick={openNotesModal}
            zIndex="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="5px"
            w="200px"
            h="48px"
            // mr={"4px"}
            ml={"4px"}
            // mx="auto"
            left={{ lg: "80%", xl: "80%" }}
            backgroundImage="linear-gradient(to right top, #0d7e54, #007774, #006d94, #005ea7, #1146a0)"
          >
            <Image w="16px" src="/assets/imgs/white-spark.png" />
            <Box display="flex" flexDir="column">
              <Text
                textAlign="left"
                fontSize="10px"
                fontWeight="700"
                color="white"
              >
                Aira - Generate Notes
              </Text>
            </Box>
          </Box>
        </Flex>
      </Stack>

      <DisplayNotesModal
        ref={airaTranscribeGenerateNotes}
        closeAction={() => {
          fetchPatientHealthData();
          console.log("calling patient health data and doctor notes");
          fetchDoctorNotes();
          airaTranscribeGenerateNotes.current.closeModal();
        }}
        modalHeader="Aira: Notes"
        modalWidth={voice ? "100vw" : ""}
        // modalHeight={"100vh"}
        hasFooter={false}
        modalBody={
          <>
            {voice || initialVoice ? (
              <TranscriptNotesCard
                patientId={patientId}
                doctorId={doctorId}
                appointmentId={appointmentId}
                showSuccessToast={showSuccessToast}
                showErrorToast={showErrorToast}
                transcriptionText={
                  initialVoice ? initialVoice + " " + voice : voice
                }
                // transcriptionText={
                //   demoText
                // }
                transcriptionArray={[]}
              />
            ) : (
              "No Data Available, Please record something before using this feature."
            )}
          </>
        }
        modalFooterBtn={
          <>
            <PrimaryButton
              variant="deleteBtn"
              buttonText="Close"
              w="200px"
              mx="auto"
              onClick={() => airaTranscribeGenerateNotes.current.closeModal()}
            />
          </>
        }
      />
    </Flex>
  );
}

export default MobileTranscribeNotes;
